/*
  Carrousel en coverflow.

  Portage en JS natif du composant React fourni. Les partis pris d'origine
  sont conserves parce qu'ils sont justes :

  - Une seule source de verite : `pos`, l'indice fractionnaire de la carte
    au centre. Tout le reste s'en deduit.

  - On peint directement dans le DOM, pas via un etat. Soixante mises a
    jour par seconde repeindraient toutes les cartes pour des nombres que
    personne n'a besoin de voir passer.

  - La boucle se fait en repliant la distance du cote le plus court de
    l'anneau. Pas de noeud clone, pas de DOM reordonne.

  - L'inclinaison et le recul s'attenuent avec la distance (exposant < 1) :
    une rampe lineaire refermerait la deuxieme carte sur elle-meme.
*/

(() => {
  class Coverflow {
    constructor(root) {
      this.root = root;
      this.frame = root.querySelector('.coverflow__frame');
      this.rail = root.querySelector('.coverflow__rail');
      this.cards = Array.from(root.querySelectorAll('.coverflow__card'));
      this.dots = Array.from(root.querySelectorAll('.coverflow__dot'));
      this.count = this.cards.length;
      if (!this.count) return;

      const d = root.dataset;
      this.rotate = parseFloat(d.rotate) || 44;
      this.depth = parseFloat(d.depth) || 0.6;
      this.falloff = parseFloat(d.falloff) || 0.56;
      this.fade = parseFloat(d.fade) || 0.1;
      this.gap = parseFloat(d.gap) || 0.05;
      this.loop = d.loop !== 'false';

      this.pos = 0;
      // Ou la glissade en cours se dirige. Partir de `pos` avalerait une
      // touche pressee en plein vol, avant que l'arrondi ne bouge.
      this.target = 0;
      this.width = 0;
      this.raf = null;
      this.drag = null;
      this.selected = 0;

      this.paint = this.paint.bind(this);
      this.measure = this.measure.bind(this);

      this.wire();
      this.measure();
      // `select` sort tot quand l'index ne change pas : au chargement il ne
      // passerait donc jamais. On pose l'etat initial explicitement.
      this.syncLinkFocus();
      new ResizeObserver(this.measure).observe(this.frame);
    }

    /* Carte entiere la plus proche, repliee dans 0..count-1. */
    indexAt(pos) {
      return ((Math.round(pos) % this.count) + this.count) % this.count;
    }

    clamp(pos) {
      return this.loop ? pos : Math.max(0, Math.min(this.count - 1, pos));
    }

    measure() {
      // La largeur de carte commande le pas, le recul et la perspective :
      // c'est la seule chose qui vaille d'etre mesuree.
      this.width = this.cards[0] ? this.cards[0].offsetWidth : 0;
      this.paint();
    }

    paint() {
      if (!this.width) return;
      const pitch = this.width * (1 + this.gap);

      this.cards.forEach((card, index) => {
        let offset = index - this.pos;
        if (this.loop) {
          offset = ((offset % this.count) + this.count) % this.count;
          if (offset > this.count / 2) offset -= this.count;
        }

        const distance = Math.abs(offset);
        const ramp = Math.pow(distance, this.falloff);
        // Plafonnee en deca de la tranche : une carte lointaine ne doit
        // jamais tourner completement le dos.
        const tilt = Math.min(this.rotate * ramp, 82) * Math.sign(offset);

        card.style.transform =
          `translateX(calc(-50% + ${offset * pitch}px)) ` +
          `translateZ(${-this.depth * this.width * ramp}px) ` +
          `rotateY(${-tilt}deg)`;

        // Une carte est teleportee a l'autre bout de l'anneau a exactement
        // un demi-tour : elle doit avoir disparu avant, sinon le saut se voit.
        const edge = this.loop ? Math.min(1, Math.max(0, this.count / 2 - distance)) : 1;
        card.style.opacity = String(Math.max(0, 1 - this.fade * distance) * edge);
        card.style.zIndex = String(100 - Math.round(distance));
        card.dataset.active = distance < 0.5 ? 'true' : 'false';
      });
    }

    settle(target) {
      if (this.raf !== null) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      this.target = target;
      this.select(this.indexAt(target));

      /*
        Rien a animer : on ne programme AUCUNE image. Sans cette sortie, un
        simple clic laissait une animation en cours le temps d'une image, et
        le carrousel se declarait "en mouvement" a un moment ou il ne bougeait
        pas -- de quoi faire passer un clic pour un geste d'arret.
      */
      if (Math.abs(this.target - this.pos) < 0.0004) {
        this.pos = this.target;
        this.paint();
        return;
      }

      const step = () => {
        const remaining = this.target - this.pos;
        if (Math.abs(remaining) < 0.0004) {
          this.pos = this.target;
          this.paint();
          this.raf = null;
          return;
        }
        // Amortissement exponentiel, pas un ressort : on ne veut pas de
        // depassement sur un objet qu'on vend.
        this.pos += remaining * 0.16;
        this.paint();
        this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }

    goTo(index) {
      // Prendre le chemin le plus court plutot que derouler tout l'anneau.
      const target = this.loop
        ? index + Math.round((this.target - index) / this.count) * this.count
        : index;
      this.settle(this.clamp(target));
    }

    nudge(by) {
      this.settle(this.clamp(Math.round(this.target) + by));
    }

    select(index) {
      if (index === this.selected) return;
      this.selected = index;
      this.dots.forEach((dot, i) =>
        dot.setAttribute('aria-current', i === index ? 'true' : 'false')
      );
      this.renderCaption();
      this.syncLinkFocus();
    }

    /*
      TABULATION MOUVANTE. Les dix cartes sont cliquables, mais mettre dix
      liens dans l'ordre de tabulation obligerait a traverser tout le
      carrousel pour atteindre la suite de la page. Un seul est tabulable a
      la fois : celui de la carte choisie. Le chemin au clavier reste complet
      -- Tab jusqu'au cadre, fleches pour choisir un plateau, Tab pour
      atteindre son lien, Entree pour l'ouvrir -- et il ne coute que deux
      arrets de tabulation quel que soit le nombre de plateaux.

      Et le focus SUIT la selection : sans cela, une fleche pressee depuis le
      lien deplacerait le carrousel en laissant le focus sur un lien devenu
      non tabulable, c'est-a-dire nulle part. `preventScroll` parce que
      redonner le focus ne doit jamais faire sauter la page.

      Pose ici et non dans `paint()` : l'etat ne depend que de la carte
      centrale, qui change quelques fois par minute, quand `paint()` tourne a
      chaque image.
    */
    syncLinkFocus() {
      const focused = document.activeElement;
      let follow = false;
      this.cards.forEach((card, i) => {
        const link = card.querySelector('.coverflow__link');
        if (!link) return;
        if (i === this.selected) {
          link.removeAttribute('tabindex');
        } else {
          if (link === focused) follow = true;
          link.setAttribute('tabindex', '-1');
        }
      });
      if (!follow) return;
      const card = this.cards[this.selected];
      const link = card && card.querySelector('.coverflow__link');
      if (link) link.focus({ preventScroll: true });
    }

    renderCaption() {
      const card = this.cards[this.selected];
      if (!card) return;
      const set = (sel, value, attr) => {
        const el = this.root.querySelector(sel);
        if (!el) return;
        if (attr) el.setAttribute(attr, value || '');
        else el.textContent = value || '';
      };
      set('.coverflow__name', card.dataset.name);
      set('.coverflow__range', card.dataset.range);
      set('.coverflow__price', card.dataset.price);
      set('.coverflow__size', card.dataset.size);
      // Le href du CTA n'est plus touche ici : il pointe sur la collection
      // des plateaux, la meme quelle que soit la carte centrale. Le reecrire
      // a chaque defilement remettait l'URL du produit centre.
    }

    /*
      CLIC OU FIN DE GLISSEMENT ?

      LA REGLE EST DESORMAIS PERMISSIVE : on navigue, sauf preuve franche d'un
      glissement. Le rapport des couts l'impose -- une navigation involontaire
      se repare d'un retour arriere, un clic avale donne un site casse. Le
      humain a d'ailleurs vecu exactement ca.

      UNE SEULE raison de ne pas naviguer : LE DEPLACEMENT HORIZONTAL MAXIMAL
      depasse la tolerance, 12 px au pointeur fin, 16 px au doigt. C'est
      beaucoup plus large qu'avant (5 et 10) parce qu'un clic de pave tactile
      derive de plusieurs pixels pendant l'appui, et qu'on mesure le maximum
      atteint.
      HORIZONTAL seulement : le carrousel ne reagit qu'a cet axe, une derive
      verticale ne l'a jamais fait bouger et ne doit donc jamais couter un
      clic. Et bien le MAXIMUM, pas l'ecart final : un aller-retour revient a
      son point de depart et passerait sinon pour un clic immobile.

      CE QUI A ETE RETIRE, ET POURQUOI. La duree de l'appui : un appui long
      est deja traite par le systeme, qui n'active pas le lien de lui-meme ;
      y ajouter notre plafond ne pouvait qu'avaler des clics lents.
      Et surtout l'ancienne regle "l'appui a arrete un carrousel en vol" :
      elle n'etait pas une preuve de glissement, seulement la preuve qu'une
      animation tournait -- et si les images se font rares (onglet en
      arriere-plan, machine en economie d'energie), cette animation ne se
      termine jamais et TOUT clic se trouve bloque, sans fin. Constate a la
      mesure : trois clics ordinaires refuses d'affilee dans cet etat. Un vrai
      glissement est deja arrete par la tolerance ci-dessus ; arreter un
      carrousel qui glisse en ouvrant la fiche est un desagrement, un site qui
      ne repond pas est une panne.

      Et une activation au clavier (`detail` a zero) n'est jamais bloquee :
      elle ne vient d'aucun geste.

      Filet supplementaire, gratuit : pendant un vrai glissement le cadre
      capture le pointeur, et le navigateur redirige alors le clic vers le
      cadre au lieu de l'ancre. Le glissement se protege donc lui-meme, ce
      qui autorise cette regle a etre large sans risque.
    */
    blocks(event) {
      if (!event.detail) return false;
      const gesture = this.gesture;
      if (!gesture) return false;
      return gesture.travel > gesture.slop;
    }

    wire() {
      const SLOP_FINE = 12;
      const SLOP_COARSE = 16;
      const CAPTURE_AFTER = 3;

      this.frame.addEventListener('pointerdown', (e) => {
        /*
          AUCUNE CAPTURE DE POINTEUR ICI, et c'est le coeur de la correction.
          Capturer le pointeur sur le cadre des l'appui fait rediriger le
          `click` vers le CADRE au lieu de l'ancre : le lien ne recoit jamais
          son clic et la page ne change pas. Constate sur un vrai clic, page
          d'essai minimale, souris reelle : avec capture, la cible du clic est
          le cadre et l'ancre est ignoree ; sans capture, c'est toujours
          l'ancre. Relacher la capture au relachement du pointeur NE SUFFIT
          PAS -- la redirection a deja eu lieu.
          La capture est donc prise plus tard, au premier vrai deplacement,
          la ou elle sert vraiment : suivre un glissement qui sort du cadre.
        */
        if (this.raf !== null) {
          cancelAnimationFrame(this.raf);
          this.raf = null;
        }
        this.target = this.pos;
        this.drag = { id: e.pointerId, x: e.clientX, pos: this.pos, v: 0, t: performance.now() };
        this.gesture = {
          id: e.pointerId,
          x: e.clientX,
          travel: 0,
          captured: false,
          // Un doigt derive plus qu'une souris, et un pave tactile derive
          // plus qu'une souris posee.
          slop: e.pointerType === 'mouse' ? SLOP_FINE : SLOP_COARSE,
        };
      });

      this.frame.addEventListener('pointermove', (e) => {
        const gesture = this.gesture;
        if (gesture && gesture.id === e.pointerId) {
          // Le MAXIMUM atteint, pas l'ecart final : un aller-retour revient a
          // son point de depart et passerait sinon pour un clic immobile.
          // Horizontal seul : c'est le seul axe auquel le carrousel reagit.
          gesture.travel = Math.max(gesture.travel, Math.abs(e.clientX - gesture.x));

          // La capture n'est prise qu'a partir d'un vrai deplacement : un clic
          // n'en declenche aucune et son `click` atteint donc l'ancre.
          if (!gesture.captured && gesture.travel > CAPTURE_AFTER) {
            gesture.captured = true;
            try {
              this.frame.setPointerCapture(e.pointerId);
            } catch (err) {
              /* le pointeur peut avoir deja disparu */
            }
          }
        }

        const drag = this.drag;
        if (!drag || drag.id !== e.pointerId) return;
        const pitch = this.width * (1 + this.gap);
        if (!pitch) return;

        const now = performance.now();
        const previous = this.pos;
        this.pos = this.clamp(drag.pos - (e.clientX - drag.x) / pitch);
        // Cartes par seconde, pour le lance.
        drag.v = ((this.pos - previous) / Math.max(now - drag.t, 1)) * 1000;
        drag.t = now;

        this.select(this.indexAt(this.pos));
        this.paint();
      });

      const end = (e) => {
        const drag = this.drag;
        if (!drag || drag.id !== e.pointerId) return;
        this.drag = null;
        // Un lance porte, mais jamais au-dela de deux cartes.
        const carried = Math.max(-2, Math.min(2, drag.v * 0.18));
        this.settle(this.clamp(Math.round(this.pos + carried)));
      };
      /*
        Sur la FENETRE et non sur le cadre : sans capture au premier appui, un
        glissement relache en dehors du cadre ne lui reviendrait pas, et le
        carrousel resterait accroche au pointeur. `end` ne fait rien si le
        glissement n'est pas le sien, donc ecouter large ne coute rien.
      */
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);

      this.frame.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.nudge(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.nudge(1); }
      });

      this.root.querySelectorAll('[data-nudge]').forEach((b) =>
        b.addEventListener('click', () => this.nudge(Number(b.dataset.nudge)))
      );
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.root.querySelectorAll('.coverflow__link').forEach((a) =>
        a.addEventListener('click', (e) => {
          if (this.blocks(e)) e.preventDefault();
        })
      );

      // Le navigateur veut demarrer son propre glisser des qu'on tire sur un
      // lien ou une image. Il faut le lui refuser, sinon le geste est vole au
      // carrousel des la premiere carte laterale saisie.
      this.root.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }

  const boot = () => {
    document.querySelectorAll('.coverflow').forEach((el) => {
      if (el.dataset.cfReady) return;
      el.dataset.cfReady = 'true';
      new Coverflow(el);
    });
  };

  document.addEventListener('DOMContentLoaded', boot);
  // L'editeur de theme reinjecte la section sans recharger la page.
  document.addEventListener('shopify:section:load', boot);
  if (document.readyState !== 'loading') boot();

  /*
    Le defilement doux n'est arme qu'UNE FOIS LA PAGE CHARGEE, et une image
    plus tard. Raison : `scroll-behavior: smooth` ne lisse pas que les clics,
    il transforme aussi le saut d'ancre d'arrivee en animation -- et celle-la
    n'aboutit pas. Mesure en production sur
    /collections/backgammon-boards#shop-now : 22 px parcourus sur les 307
    attendus. En defilement instantane, le meme lien atterrit exactement.

    Chrome execute le saut d'ancre au chargement, pas avant : les deux
    `requestAnimationFrame` garantissent qu'on arme APRES lui et non pendant.
    Si le navigateur ne peint jamais -- onglet en arriere-plan -- le lissage
    n'est simplement pas arme, et le defilement reste instantane : l'echec se
    fait du bon cote.
  */
  const armSmoothScrolling = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => document.documentElement.classList.add('coverflow-smooth'));
    });
  };

  if (document.readyState === 'complete') armSmoothScrolling();
  else window.addEventListener('load', armSmoothScrolling, { once: true });
})();
